/**
 * The slice of Markdown the assistant actually emits.
 *
 * Nothing tells the model to write Markdown, but it does anyway, and asking a
 * question that spans documents brings out the whole repertoire: a pipe table
 * comparing them column by column, bullets nested two deep underneath, bold around
 * every document name. Printed raw that arrives as `| Position | View title |` and
 * a row of dashes, which is how this module came to exist.
 *
 * It stays hand-written rather than becoming a dependency: a CommonMark parser
 * brings an HTML sanitiser to configure, and the constructs below are the ones
 * that turn up in practice. Anything unrecognised stays the literal text it was,
 * which is the right failure mode for a renderer fed prose - an unsupported
 * construct looks slightly raw instead of vanishing.
 *
 * Nothing here builds an HTML string. Model output is untrusted input like anything
 * else that crosses the wire, so it becomes React elements and text nodes and
 * `dangerouslySetInnerHTML` never enters the picture - an answer containing
 * `<script>` renders as those eight characters.
 *
 * Emphasis is deliberately asterisk-only. Underscores are left alone because the
 * data here is full of them: extracted keys arrive as `source_page`, and file names
 * like `AU00110396BM_1.pdf` would turn half-italic on the way to the screen. Losing
 * `_italic_` costs nothing, since the model writes `*italic*`.
 */
import type { CSSProperties, ReactNode } from "react";

type Align = "left" | "center" | "right";

/** A bullet or numbered list, which may nest to any depth. */
interface List {
  ordered: boolean;
  items: ListItem[];
}

interface ListItem {
  text: string;
  child: List | null;
}

/** A run of lines that renders as one element. */
type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; lines: string[] }
  | { kind: "list"; list: List }
  | { kind: "code"; lines: string[] }
  | { kind: "table"; head: string[]; rows: string[][]; align: Align[] };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^(\s*)[-*\u2022]\s+(.*)$/;
const NUMBERED = /^(\s*)\d+[.)]\s+(.*)$/;
const FENCE = /^\s*```/;

/** One list item as found in the source, before the nesting is worked out. */
interface Entry {
  indent: number;
  ordered: boolean;
  text: string;
}

/** Tabs count as four columns, which is what the indent-per-depth comparison needs. */
function indentOf(whitespace: string): number {
  return whitespace.replace(/\t/g, "    ").length;
}

function matchItem(line: string): Entry | null {
  const bullet = BULLET.exec(line);
  if (bullet) return { indent: indentOf(bullet[1]), ordered: false, text: bullet[2] };
  const numbered = NUMBERED.exec(line);
  if (numbered) return { indent: indentOf(numbered[1]), ordered: true, text: numbered[2] };
  return null;
}

// ---------------------------------------------------------------------- tables

/** Split a table row into cells, tolerating the optional leading and trailing pipes. */
function splitRow(line: string): string[] {
  let text = line.trim();
  if (text.startsWith("|")) text = text.slice(1);
  if (text.endsWith("|")) text = text.slice(0, -1);
  return text.split("|").map((cell) => cell.trim());
}

/**
 * True for the `|---|:--:|` line under a table's header.
 *
 * This is what distinguishes a table from a paragraph that happens to contain a
 * pipe, so it is checked before a line with pipes is treated as a row.
 */
function isDelimiterRow(line: string): boolean {
  if (!line.includes("-")) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell));
}

function alignOf(cell: string): Align {
  const left = cell.startsWith(":");
  const right = cell.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  return "left";
}

// ---------------------------------------------------------------------- blocks

/**
 * Group lines into blocks.
 *
 * Single pass. A fence swallows everything up to its close (or to the end, when the
 * model forgets to close it); a table is claimed only on the header/delimiter pair;
 * a list run is gathered whole and its nesting resolved afterwards, because the
 * shape of an item depends on the indentation of the lines that follow it.
 */
function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  const lines = text.replace(/\r\n?/g, "\n").split("\n");

  let paragraph: string[] = [];

  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", lines: paragraph });
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (FENCE.test(line)) {
      flush();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      blocks.push({ kind: "code", lines: body });
      continue;
    }

    if (line.trim() === "") {
      flush();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: "heading", level: heading[1].length, text: heading[2] });
      continue;
    }

    // A table needs its delimiter row to be recognised, so both lines are examined
    // before either is consumed.
    if (line.includes("|") && i + 1 < lines.length && isDelimiterRow(lines[i + 1])) {
      flush();
      const head = splitRow(line);
      const align = splitRow(lines[i + 1]).map(alignOf);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim() !== "" && lines[i].includes("|")) {
        const cells = splitRow(lines[i]);
        // Ragged rows are normalised to the header's width: a short row gets empty
        // cells, a long one is clipped, so every row lines up under its heading.
        rows.push(
          Array.from({ length: head.length }, (_, column) => cells[column] ?? ""),
        );
        i += 1;
      }
      i -= 1;
      blocks.push({
        kind: "table",
        head,
        rows,
        align: Array.from({ length: head.length }, (_, column) => align[column] ?? "left"),
      });
      continue;
    }

    const first = matchItem(line);
    if (first) {
      flush();
      const entries: Entry[] = [first];

      while (i + 1 < lines.length) {
        const next = lines[i + 1];

        // A blank line inside a list is normal Markdown, so it only ends the run
        // when what follows is not another item.
        if (next.trim() === "") {
          let ahead = i + 2;
          while (ahead < lines.length && lines[ahead].trim() === "") ahead += 1;
          if (ahead >= lines.length || !matchItem(lines[ahead])) break;
          i = ahead - 1;
          continue;
        }

        const item = matchItem(next);
        if (item) {
          entries.push(item);
          i += 1;
          continue;
        }

        // Indented and not an item: the wrapped remainder of the item above. That
        // is a soft wrap, so it rejoins with a space rather than keeping the break.
        if (/^\s{2,}\S/.test(next)) {
          entries[entries.length - 1].text += ` ${next.trim()}`;
          i += 1;
          continue;
        }

        break;
      }

      blocks.push({ kind: "list", list: buildList(entries, 0, entries[0].indent)[0] });
      continue;
    }

    paragraph.push(line);
  }

  flush();
  return blocks;
}

/**
 * Turn a flat run of items into a tree, by indentation.
 *
 * Anything indented further than the level being built belongs to the item above
 * it, which is what keeps "Proposed drawings are Position 2" underneath "Proposed
 * vs Survey drawings" instead of beside it.
 */
function buildList(entries: Entry[], start: number, indent: number): [List, number] {
  const items: ListItem[] = [];
  const ordered = entries[start].ordered;
  let i = start;

  while (i < entries.length && entries[i].indent >= indent) {
    if (entries[i].indent > indent) {
      const [child, next] = buildList(entries, i, entries[i].indent);
      // A deeper item with nothing above it can only come from malformed input;
      // promoting it to this level keeps its text on screen.
      if (items.length > 0) items[items.length - 1].child = child;
      else items.push(...child.items);
      i = next;
      continue;
    }
    items.push({ text: entries[i].text, child: null });
    i += 1;
  }

  return [{ ordered, items }, i];
}

// ---------------------------------------------------------------------- inline

/** `**bold**`, `*italic*` and `` `code` ``, in one scan. */
const INLINE = /\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`]+)`/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let index = 0;

  INLINE.lastIndex = 0;
  let match = INLINE.exec(text);
  while (match !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const key = `${keyPrefix}i${index}`;
    index += 1;

    if (match[1] !== undefined) nodes.push(<strong key={key}>{match[1]}</strong>);
    else if (match[2] !== undefined) nodes.push(<em key={key}>{match[2]}</em>);
    else nodes.push(<code key={key} className="md__code">{match[3]}</code>);

    last = INLINE.lastIndex;
    match = INLINE.exec(text);
  }

  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/**
 * Render one line's worth of inline markup per line, separated by breaks.
 *
 * Markdown would join these with a space; they are kept apart because the model
 * uses a bare newline to mean one, and the `white-space: pre-wrap` this replaced
 * honoured that. Reflowing the text now would be a visible regression on every
 * answer that lists things without bulleting them.
 */
function renderLines(lines: string[], keyPrefix: string): ReactNode[] {
  return lines.flatMap((line, index) => {
    const rendered = renderInline(line, `${keyPrefix}l${index}`);
    return index === 0 ? rendered : [<br key={`${keyPrefix}br${index}`} />, ...rendered];
  });
}

// ----------------------------------------------------------------------- render

function renderList(list: List, keyPrefix: string): ReactNode {
  const items = list.items.map((item, index) => {
    const key = `${keyPrefix}x${index}`;
    return (
      <li key={key}>
        {renderInline(item.text, key)}
        {item.child && renderList(item.child, `${key}c`)}
      </li>
    );
  });

  return list.ordered ? (
    <ol className="md__list" key={keyPrefix}>
      {items}
    </ol>
  ) : (
    <ul className="md__list" key={keyPrefix}>
      {items}
    </ul>
  );
}

/** Render Markdown-ish assistant prose as elements. */
export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks = parseBlocks(text);

  return (
    <div className={className ? `md ${className}` : "md"}>
      {blocks.map((block, index) => {
        const key = `b${index}`;

        switch (block.kind) {
          case "heading":
            // The bubble is not a document, so headings step down to strong text
            // rather than opening a type scale inside a chat reply.
            return (
              <div className="md__heading" key={key} role="heading" aria-level={block.level}>
                {renderInline(block.text, key)}
              </div>
            );
          case "code":
            return (
              <pre className="md__pre" key={key}>
                <code>{block.lines.join("\n")}</code>
              </pre>
            );
          case "list":
            return renderList(block.list, key);
          case "table": {
            const cellStyle = (column: number): CSSProperties => ({
              textAlign: block.align[column],
            });
            return (
              <div className="md__tablewrap" key={key}>
                <table className="md__table">
                  <thead>
                    <tr>
                      {block.head.map((cell, column) => (
                        <th key={`${key}h${column}`} style={cellStyle(column)}>
                          {renderInline(cell, `${key}h${column}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={`${key}r${rowIndex}`}>
                        {row.map((cell, column) => (
                          <td key={`${key}r${rowIndex}c${column}`} style={cellStyle(column)}>
                            {renderInline(cell, `${key}r${rowIndex}c${column}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          case "paragraph":
            return (
              <p className="md__p" key={key}>
                {renderLines(block.lines, key)}
              </p>
            );
        }
      })}
    </div>
  );
}
